import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import InputHandler from 'Components/InputHandler';
import { withFormContext } from 'Containers/FormContextHandler';
import * as React from 'react';
import { filterByDocumentType } from 'Utility/documentHandling';
import colors from '../../../../../../config/theme/colors';

import type { IFormContext } from 'Containers/FormContextHandler';

const SymptomsAndLocationsForm = ({ documents, editing, formData, fm }: IFormContext): React.JSX.Element => {
  const treatmentId = formData.document.treatmentPeriodId;

  const primarySymptomDocument: ISymptomsAndLocations | undefined = filterByDocumentType(
    documents ?? [],
    'symptomsAndLocations',
  ).find(d => !d._editing && d.treatmentPeriodId === treatmentId && d.primarySymptom);

  return (
    <React.Fragment>
      <FormSection>
        <FormRow title="diagnosis.ninmt.symptomsAndLocations.startDate">
          <InputHandler
            type="PartialDate"
            name="startDate"
            formData={formData}
            editing={!!editing}
            dateDefault="now"
            dateHook={{ dateHookCeiling: formData.document.endDate }}
            isNotCancellable
          />
        </FormRow>
        <FormRow title="diagnosis.ninmt.symptomsAndLocations.endDate">
          <InputHandler
            type="PartialDate"
            name="endDate"
            formData={formData}
            editing={!!editing}
            dateHook={{ dateHookFloor: formData.document.startDate }}
          />
        </FormRow>
      </FormSection>
      <FormSection>
        <FormRow title="diagnosis.ninmt.symptomsAndLocations.symptom">
          <InputHandler
            type="Select"
            name="symptom"
            formData={formData}
            editing={!!editing}
            options={['pain', 'tinnitus', 'other']}
            optionFormatter={(name: string | number): string => fm(`diagnosis.ninmt.symptomsAndLocations.opts.${name}`)}
            placeholder={'diagnosis.ninmt.symptomsAndLocations.symptomPlaceholder'}
            dependentFieldsList={(): string[] => ['mentalSymptom', 'symptomDescription', 'symptomLocation']}
          />
        </FormRow>
        <FormRow condition={formData.document.symptom === 'other'}>
          <div style={{ display: 'flex' }}>
            <InputHandler
              name="mentalSymptom"
              formData={formData}
              type="CheckboxSingle"
              editing={!!editing}
              option="true"
              showLabel
              optionFormatter={() => fm('diagnosis.ninmt.symptomsAndLocations.mentalSymptom')}
              dependentFieldsList={() => ['averageSymptomIntensity']}
            />
            <span style={{ color: colors.secondaryText, paddingLeft: '0.5rem' }}>
              {`- ${fm('diagnosis.ninmt.symptomsAndLocations.mentalSymptomInfoText')}`}
            </span>
          </div>
        </FormRow>
        {formData.document.symptom === 'other' ? (
          <FormRow title="diagnosis.ninmt.symptomsAndLocations.symptomDescription">
            <InputHandler
              type="TextArea"
              name="symptomDescription"
              formData={formData}
              editing={!!editing}
              placeholder="diagnosis.ninmt.symptomsAndLocations.symptomDescriptionPlaceholder"
              rows={1}
              maxLength={40}
              width={40}
            />
          </FormRow>
        ) : (
          <FormRow title="diagnosis.ninmt.symptomsAndLocations.symptomLocation">
            <InputHandler
              type="Select"
              name="symptomLocation"
              formData={formData}
              editing={!!editing}
              options={
                formData.document.symptom === 'pain'
                  ? [
                      'upperLimbLeft',
                      'upperLimbRight',
                      'upperLimbBoth',
                      'lowerLimbLeft',
                      'lowerLimbRight',
                      'lowerLimbBoth',
                      'body',
                      'hipOrGenitalia',
                      'headArea',
                      'widespreadPainSymptoms',
                    ]
                  : ['right', 'left', 'middle']
              }
              optionFormatter={(name: string | number): string =>
                fm(`diagnosis.ninmt.symptomsAndLocations.opts.${name}`)
              }
              placeholder={'diagnosis.ninmt.symptomsAndLocations.symptomLocationPlaceholder'}
            />
          </FormRow>
        )}
        <FormRow condition={formData.document.symptom}>
          <div style={{ display: 'flex' }}>
            <InputHandler
              name="primarySymptom"
              formData={formData}
              type="CheckboxSingle"
              editing={!!editing}
              option="true"
              showLabel
              optionFormatter={() => fm('diagnosis.ninmt.symptomsAndLocations.primarySymptom')}
              disabled={!!primarySymptomDocument}
            />
            {primarySymptomDocument && (
              <span style={{ color: colors.secondaryText, paddingLeft: '0.5rem' }}>
                {'- '}
                {fm('diagnosis.ninmt.symptomsAndLocations.primarySymptomAlreadyExists', {
                  value:
                    primarySymptomDocument.symptom !== 'other' && primarySymptomDocument.symptomLocation ? (
                      <React.Fragment>
                        {fm(`diagnosis.ninmt.symptomsAndLocations.opts.${primarySymptomDocument.symptom}`)}
                        {`: ${fm(
                          `diagnosis.ninmt.symptomsAndLocations.opts.${primarySymptomDocument.symptomLocation}`,
                        )}`}
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        {fm(`diagnosis.ninmt.symptomsAndLocations.opts.${primarySymptomDocument.symptom}`)}
                        {primarySymptomDocument.symptomDescription && (
                          <React.Fragment>
                            {primarySymptomDocument.symptomDescription.length > 20
                              ? `: ${primarySymptomDocument.symptomDescription.slice(0, 20).trim()}...`
                              : `: ${primarySymptomDocument.symptomDescription}`}
                          </React.Fragment>
                        )}
                      </React.Fragment>
                    ),
                  strong: (chunks: string) => (
                    <span key={String(chunks)} style={{ fontWeight: 600 }}>
                      {React.Children.toArray(chunks)}
                    </span>
                  ),
                })}
              </span>
            )}
          </div>
        </FormRow>
        <FormRow
          title="diagnosis.ninmt.symptomsAndLocations.averagePainTitle"
          description={fm('diagnosis.ninmt.symptomsAndLocations.averagePainDesc')}
          condition={!formData.document.mentalSymptom}
        >
          <InputHandler
            type="NumberField"
            name="averageSymptomIntensity"
            formData={formData}
            editing={!!editing}
            min={0}
            max={10}
            precision={1}
            enableTrailingZero
            disablePlaceholderFormatting
            placeholder="0.0-10.0"
            width={8}
          />
        </FormRow>
        <FormRow
          title="diagnosis.ninmt.symptomsAndLocations.averageHarmTitle"
          description={fm('diagnosis.ninmt.symptomsAndLocations.averagePainDesc')}
          condition={!formData.document.mentalSymptom}
        >
          <InputHandler
            type="NumberField"
            name="averageSymptomHarm"
            formData={formData}
            editing={!!editing}
            min={0}
            max={10}
            precision={1}
            enableTrailingZero
            disablePlaceholderFormatting
            placeholder="0.0-10.0"
            width={8}
          />
        </FormRow>
        <FormRow title="diagnosis.ninmt.symptomsAndLocations.additionalInformation">
          <InputHandler
            type="TextArea"
            name="additionalInformation"
            formData={formData}
            editing={!!editing}
            placeholder={'diagnosis.ninmt.symptomsAndLocations.additionalInformationPlaceholder'}
          />
        </FormRow>
      </FormSection>
    </React.Fragment>
  );
};

export default withFormContext(SymptomsAndLocationsForm);
